import cn from 'classnames';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import texts from './desktop-table.texts.json';
import styles from './desktop-table.module.scss';
import { formatDate } from '../format/format-date';
import { formatPrice } from '../format/format-price';
import { formatFloor } from '../format/format-floor';
import { formatAddress } from '../format/format-address';
import type { CompletedDealItem } from '../types/mapping';

type Props = {
  items: CompletedDealItem[];
  className?: string;
  enableSortByAddress?: boolean;
};

const columnHelper = createColumnHelper<CompletedDealItem>();

export const columns = [
  columnHelper.accessor('saleDate', {
    id: 'saleDate',
    header: texts.saleDate,
    cell: (saleDate) => formatDate(saleDate.getValue()),
  }),
  columnHelper.accessor('address', {
    id: 'address',
    header: texts.address,
    enableSorting: false,
    cell: (address) => formatAddress(address.getValue()),
  }),
  columnHelper.accessor('rooms', {
    id: 'rooms',
    header: texts.rooms,
  }),
  columnHelper.accessor('buildingMR', {
    id: 'buildingMR',
    header: texts.buildingMR,
  }),
  columnHelper.accessor('floor', {
    id: 'floor',
    header: texts.floor,
    cell: (floor) => formatFloor(floor.getValue()),
  }),
  columnHelper.accessor('price', {
    id: 'price',
    header: texts.price,
    cell: (price) => formatPrice(price.getValue()),
  }),
];

export function DesktopTable({ items, className }: Props) {
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table className={cn(styles.table, className)}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <th
                  key={header.id}
                  className={cn(
                    header.id === 'address' && styles.addressCol,
                    header.id === 'propertyType' && styles.propertyTypeCol,
                    header.id === 'price' && styles.priceCol,
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
